import { api, type paths } from './client';
import { ApiStatusError } from './templates';
import type { components } from './types.gen';

export type ResponseStatus = components['schemas']['AdminResponseStatus'];
export type ResponseStatusUpdate = components['schemas']['AdminResponseStatusUpdate'];
export type ResponseStatusBadge = components['schemas']['ResponseStatusBadge'];

export async function getAdminResponseStatus(): Promise<ResponseStatus> {
  const { data, error, response } = await api.GET('/admin/work-availability/status');
  if (!response.ok || !data) throwApiError(response.status, error, 'ステータスを読み込めませんでした。');
  return data.status;
}

export async function updateAdminResponseStatus(body: ResponseStatusUpdate): Promise<ResponseStatus> {
  const { data, error, response } = await api.PUT('/admin/work-availability/status', { body });
  if (!response.ok || !data) throwApiError(response.status, error, 'ステータスを保存できませんでした。');
  window.dispatchEvent(new CustomEvent('casticart:response-status-updated'));
  return data.status;
}

export async function getResponseStatusBadges(adminIds: number[]): Promise<ResponseStatusBadge[]> {
  const { data, error, response } = await api.GET('/work-availability/badges', { params: { query: { adminIds: adminIds.join(',') } } });
  if (!response.ok || !data || !Array.isArray(data.items)) throwApiError(response.status, error, 'ステータスを読み込めませんでした。');
  return data.items;
}

export async function updateAdminWorkAvailabilityEntry(entryId: string, body: EntryCreate & { expectedRevision: number }) {
  const { data, error, response } = await api.PUT('/admin/work-availability/entries/{entryId}', { params: { path: { entryId } }, body });
  if (!response.ok || !data) throwApiError(response.status, error, '予定を変更できませんでした。');
  return data.entry;
}

type AvailabilityResponse = NonNullable<
  paths['/admin/work-availability']['get']['responses'][200]['content']
>['application/json'];
type SettingsUpdate = NonNullable<
  paths['/admin/work-availability/settings']['put']['requestBody']
>['content']['application/json'];
type EntryCreate = NonNullable<
  paths['/admin/work-availability/entries']['post']['requestBody']
>['content']['application/json'];
type EntryBatchCreate = NonNullable<
  paths['/admin/work-availability/entries/batch']['post']['requestBody']
>['content']['application/json'];

function throwApiError(status: number, error: unknown, fallback: string): never {
  const statusError = new ApiStatusError(status);
  const record = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  statusError.message = String(record.message || fallback);
  throw statusError;
}

export async function getAdminWorkAvailability(): Promise<AvailabilityResponse> {
  const { data, error, response } = await api.GET('/admin/work-availability');
  if (!response.ok || !data) throwApiError(response.status, error, '勤務状況の取得に失敗しました。');
  return data as AvailabilityResponse;
}

export async function updateAdminWorkAvailabilitySettings(body: SettingsUpdate) {
  const { data, error, response } = await api.PUT('/admin/work-availability/settings', { body });
  if (!response.ok || !data) throwApiError(response.status, error, '勤務状況設定の保存に失敗しました。');
  return data;
}

export async function claimAdminWorkAvailabilityPrompt() {
  const { data, error, response } = await api.POST('/admin/work-availability/daily-prompt/claim');
  if (!response.ok || !data) throwApiError(response.status, error, '日次確認の取得に失敗しました。');
  return data;
}

export async function reserveAdminWorkAvailabilityPrompt() {
  const { data, error, response } = await withPromptTimeout(signal => api.POST('/admin/work-availability/daily-prompt/reserve', { signal }));
  if (!response.ok || !data) throwApiError(response.status, error, '日次確認の取得に失敗しました。');
  return data;
}

export async function acknowledgeAdminWorkAvailabilityPrompt(reservationToken: string) {
  const { data, error, response } = await withPromptTimeout(signal => api.POST('/admin/work-availability/daily-prompt/acknowledge', { body: { reservationToken }, signal }));
  if (!response.ok || !data) throwApiError(response.status, error, '表示確認に失敗しました。');
  return data;
}

async function withPromptTimeout<T>(request: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try { return await request(controller.signal); }
  finally { clearTimeout(timer); }
}

export async function dismissAdminWorkAvailabilityPrompt(setup = false) {
  const { data, error, response } = await api.POST('/admin/work-availability/daily-prompt/dismiss', {
    body: { setup },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '日次確認を閉じられませんでした。');
  return data;
}

export async function createAdminWorkAvailabilityEntry(body: EntryCreate) {
  const { data, error, response } = await api.POST('/admin/work-availability/entries', { body });
  if (!response.ok || !data) throwApiError(response.status, error, '勤務予定の登録に失敗しました。');
  return data;
}

export async function createAdminWorkAvailabilityEntryBatch(body: EntryBatchCreate) {
  const { data, error, response } = await api.POST('/admin/work-availability/entries/batch', { body });
  if (!response.ok || !data) throwApiError(response.status, error, '勤務予定の一括登録に失敗しました。');
  return data;
}

export async function cancelAdminWorkAvailabilityEntry(entryId: string, expectedRevision: number) {
  const { data, error, response } = await api.DELETE('/admin/work-availability/entries/{entryId}', {
    params: { path: { entryId } },
    body: { expectedRevision },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '勤務予定の取消に失敗しました。');
  return data;
}

export async function endCurrentAdminWorkAvailability(expectedRevision: number) {
  const { data, error, response } = await api.POST('/admin/work-availability/end-current', {
    body: { expectedRevision },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '業務終了の設定に失敗しました。');
  return data;
}
